import React, { useRef, useState } from 'react';
import SvgIcon from '../layout/svg-icons/svg-icons';
import { Platforms } from './formContext';

const platforms: Platforms[] = Object.values(Platforms) as Platforms[];

interface PlatformDropdownProps {
  value: string;
  onChange: (value: string) => void;
  errorText?: string;
}

export const PlatformDropdown: React.FC<PlatformDropdownProps> = ({ value, onChange, errorText }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  React.useEffect(() => {
    setFilter(value);
  }, [value]);

  const filteredPlatforms = platforms.filter((platform) =>
    platform.toLowerCase().includes(filter.toLowerCase())
  );

  const handlePlatformSelect = (platform: Platforms | string) => {
    onChange(platform);
    setFilter(platform);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((prevIndex) =>
        Math.min(prevIndex + 1, filteredPlatforms.length - 1)
      );
      scrollToHighlighted();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      scrollToHighlighted();
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && isOpen) {
        e.preventDefault();
        handlePlatformSelect(filteredPlatforms[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const scrollToHighlighted = () => {
    if (listRef.current && highlightedIndex >= 0) {
      const listItem = listRef.current.children[highlightedIndex] as HTMLElement;
      if (listItem) {
        listItem.scrollIntoView({ block: 'nearest' });
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement !== inputRef.current && (!listRef.current || !listRef.current.contains(activeElement))) {
        setIsOpen(false);
      }
    }, 100);
  };

  const menu = (
    <ul
      ref={listRef}
      className="absolute bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 w-full overflow-y-auto"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      onBlur={handleBlur}
    >
      {filteredPlatforms.map((platform, index) => (
        <li
          key={platform}
          className={`p-2 cursor-pointer ${index === highlightedIndex ? 'bg-gray-100' : ''}`}
          onMouseDown={() => handlePlatformSelect(platform)}
          onMouseEnter={() => setHighlightedIndex(index)}
        >
          {platform}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div className="relative w-full mb-2">
        <input
          ref={inputRef}
          type={'text'}
          className={`p-2 pr-10 rounded-lg h-11 w-full ${errorText ? 'border-red-500 border-2' : 'border-gray-300'}`}
          value={filter}
          onChange={(e) => {
            const newValue = e.target.value;
            setFilter(newValue);
            onChange(newValue);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Select a platform"
        />
        <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
          <SvgIcon
            width="20"
            height="20"
            fill="currentColor"
            className="bi bi-chevron-up text-sm font-bold transform rotate-180"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
            />
          </SvgIcon>
        </div>
        {isOpen && filteredPlatforms.length > 0 ? menu : null}
      </div>
      <div className={'text-red-500 italic text-sm'}>{errorText}</div>
    </>
  );
};